import { Injectable } from '@angular/core';

import { CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';

import { BetslipBetBuilderPick } from '../../../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../../../core/picks/betslip-v1-pick';
import { CriteriaType, IRewardToken } from '../../../reward-tokens.model';
import { TokenValidationInput, TokensCriteriaValidatorBase } from '../criteria-validator-base.service';

abstract class ForbiddenEntitiesValidator extends TokensCriteriaValidatorBase<IRewardToken, CriteriaType> {
    override readonly type = CriteriaType.Forbidden;

    abstract override isValid(token: IRewardToken, state: TokenValidationInput): boolean;

    protected arePicksValid(
        picks: BetslipPick[],
        forbiddenSportIds?: number[],
        forbiddenGameTemplateIds?: number[],
        forbiddenMarketTypes?: string[],
    ): boolean {
        return picks.every(
            (pick) =>
                !this.hasForbiddenSport(pick, forbiddenSportIds ?? []) &&
                !this.hasForbiddenGameTemplateId(pick, forbiddenGameTemplateIds ?? []) &&
                !this.hasForbiddenMarketType(pick, forbiddenMarketTypes ?? []),
        );
    }

    private hasForbiddenSport(pick: BetslipPick, forbiddenSports: number[]): boolean {
        return forbiddenSports.includes(pick.sportId);
    }

    private hasForbiddenGameTemplateId(pick: BetslipPick, forbiddenGameTemplates: number[]): boolean {
        return BetslipV1Pick.isPick(pick) && forbiddenGameTemplates.includes(pick.market.templateId);
    }

    protected hasForbiddenMarketType(pick: BetslipPick, forbiddenMarketTypes: string[]): boolean {
        const isBetBuilderForbidden = forbiddenMarketTypes.includes('BetBuilder');
        //should we add any check for angstrom Market ?
        if (isBetBuilderForbidden) {
            return BetslipBetBuilderPick.isPick(pick);
        }

        const marketType = !!pick.market && pick.market.marketType;

        return !!marketType && forbiddenMarketTypes.includes(marketType);
    }
}

@Injectable()
export class ForbiddenOddsBoostValidator extends ForbiddenEntitiesValidator {
    constructor(private userSettings: SyncUserSettingsService) {
        super();
    }

    isValid(_: IRewardToken, state: TokenValidationInput): boolean {
        const configSettings = this.userSettings.configSettings;

        return this.arePicksValid(
            state.selectedPicks,
            configSettings.oddsBoostForbiddenSportIds,
            configSettings.oddsBoostForbiddenGameTemplateIds,
            configSettings.oddsBoostForbiddenMarketTypes,
        );
    }
}

@Injectable()
export class ForbiddenRiskFreeValidator extends ForbiddenEntitiesValidator {
    constructor(private userSettings: SyncUserSettingsService) {
        super();
    }

    isValid(_: IRewardToken, state: TokenValidationInput): boolean {
        const configSettings = this.userSettings.configSettings;

        return this.arePicksValid(
            state.selectedPicks,
            configSettings.riskFreeForbiddenSportIds,
            configSettings.riskFreeForbiddenGameTemplateIds,
            configSettings.riskFreeForbiddenMarketTypes,
        );
    }
}

@Injectable()
export class ForbiddenFreebetValidator extends ForbiddenEntitiesValidator {
    private readonly forbiddenGameTemplates = new Set<number>(this.tokensConfig.tokens.forbiddenFreebetTemplateCategoryIds);

    constructor(private tokensConfig: CrmConfig) {
        super();
    }

    isValid(_: IRewardToken, state: TokenValidationInput): boolean {
        return state.selectedPicks.every(
            (pick) => !this.hasForbiddenMarketType(pick, this.tokensConfig.tokens.forbiddenFreebetMarketTypeIds) && !this.hasForbiddenTemplate(pick),
        );
    }

    private hasForbiddenTemplate(pick: BetslipPick): boolean {
        return BetslipV1Pick.isPick(pick) && this.forbiddenGameTemplates.has(pick.market.templateCategoryId ?? 0);
    }
}
